import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { planKeys } from '@entities/Plan';
import { toast } from '@shared/ui';
import {
  abandonRun,
  fetchDemo,
  fetchDemoInfo,
  fetchRun,
  fetchStoriesHub,
  fetchStoryMistakes,
  sendChapterInterest,
  sendDemoTextTurn,
  sendDemoVoiceTurn,
  sendTextTurn,
  sendVoiceTurn,
  startBoss,
  startDaily,
  startDemo,
  startStory,
  type LearnerLevel,
  type StoryRun,
  type TurnResponse,
} from '../api/storiesApi';

export const storiesKeys = {
  hub: ['stories', 'hub'] as const,
  run: (id: string) => ['stories', 'run', id] as const,
  mistakes: ['stories', 'mistakes'] as const,
  demoInfo: ['stories', 'demo-info'] as const,
  demo: (id: string) => ['stories', 'demo', id] as const,
};

/** Хаб игры. Сервер начислил жетоны за пройденную без начисления миссию прямо при этом открытии («жетон — всегда за
 *  прохождение», 2026-10-22) — тост и свежий баланс; в запросе, а не в компоненте: тост один, сколько бы карточек ни
 *  смотрело на хаб. */
export function useStoriesHubQuery(enabled = true) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useQuery({
    queryKey: storiesKeys.hub,
    enabled,
    queryFn: async () => {
      const hub = await fetchStoriesHub();
      const amount = hub.daily.just_granted;
      if (amount) {
        toast({ tone: 'success', title: t('stories.reward.doneTitle'), message: t('stories.reward.text', { count: amount }) });
        void qc.invalidateQueries({ queryKey: planKeys.quota });
        void qc.invalidateQueries({ queryKey: planKeys.billing });
      }
      return hub;
    },
    staleTime: 15_000,
  });
}

/** Прохождение или демо (`demo`: без входа, своё API; устаревшее демо — 404 без повторов). */
export function useStoryRunQuery(runId: string | undefined, demo = false) {
  return useQuery({
    queryKey: demo ? storiesKeys.demo(runId ?? '') : storiesKeys.run(runId ?? ''),
    queryFn: () => (demo ? fetchDemo(runId!) : fetchRun(runId!)),
    enabled: !!runId,
    staleTime: Infinity,
    retry: demo ? false : 1,
  });
}

export function useDemoInfoQuery() {
  return useQuery({ queryKey: storiesKeys.demoInfo, queryFn: fetchDemoInfo, staleTime: 30_000, retry: 1 });
}

/** Новое демо: кладём в кэш, чтобы экран игры не грузил его второй раз. */
export function useStartDemoMutation() {
  const qc = useQueryClient();
  return useMutation<StoryRun, unknown, LearnerLevel | undefined>({
    mutationFn: (level) => startDemo(level),
    onSuccess: (run) => {
      qc.setQueryData(storiesKeys.demo(run.id), run);
      void qc.invalidateQueries({ queryKey: storiesKeys.demoInfo });
    },
  });
}

export function useStoryMistakesQuery() {
  return useQuery({ queryKey: storiesKeys.mistakes, queryFn: fetchStoryMistakes, staleTime: 15_000 });
}

export type StartTarget = { kind: 'story'; templateId: string; restart?: boolean } | { kind: 'daily' } | { kind: 'boss' };

/** Старт или продолжение: прохождение сразу кладём в кэш, чтобы экран игры не грузил его второй раз. Старт тратит
 *  жетоны — баланс перечитываем. */
export function useStartRunMutation() {
  const qc = useQueryClient();
  return useMutation<StoryRun, unknown, StartTarget>({
    mutationFn: (target) =>
      target.kind === 'daily' ? startDaily() : target.kind === 'boss' ? startBoss() : startStory(target.templateId, target.restart),
    onSuccess: (run) => {
      qc.setQueryData(storiesKeys.run(run.id), run);
      void qc.invalidateQueries({ queryKey: storiesKeys.hub });
      void qc.invalidateQueries({ queryKey: planKeys.quota });
      void qc.invalidateQueries({ queryKey: planKeys.billing });
    },
  });
}

export type TurnInput = { runId: string; turn: number } & ({ text: string } | { audio: Blob });

export function useTurnMutation(demo = false) {
  const qc = useQueryClient();
  return useMutation<TurnResponse, unknown, TurnInput>({
    mutationFn: (input) =>
      demo
        ? 'audio' in input
          ? sendDemoVoiceTurn(input.runId, input.audio, input.turn)
          : sendDemoTextTurn(input.runId, input.text, input.turn)
        : 'audio' in input
          ? sendVoiceTurn(input.runId, input.audio, input.turn)
          : sendTextTurn(input.runId, input.text, input.turn),
    onSuccess: (data) => {
      if (demo) {
        qc.setQueryData(storiesKeys.demo(data.run.id), data.run);
        return;
      }
      qc.setQueryData(storiesKeys.run(data.run.id), data.run);
      if (data.run.status !== 'active') {
        void qc.invalidateQueries({ queryKey: storiesKeys.hub });
        void qc.invalidateQueries({ queryKey: storiesKeys.mistakes });
      }
    },
  });
}

export function useAbandonRunMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: abandonRun,
    onSuccess: () => void qc.invalidateQueries({ queryKey: storiesKeys.hub }),
  });
}

export function useChapterInterestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chapterId, choice }: { chapterId: string; choice: 'yes' | 'like' }) => sendChapterInterest(chapterId, choice),
    onSuccess: () => void qc.invalidateQueries({ queryKey: storiesKeys.hub }),
  });
}
